import { dia } from '@joint/core'
import * as joint from '@joint/plus'
import { Artifact, ArtifactType, LinkType } from '@/models'
import { GraphLayout } from './graph-layout'
import { makeLink, makeTypeContainer } from './rappid-helpers'
import { useArtifactCacheStore } from '@/stores/artifactCacheStore'
import RappidContext from './rappid-context'
import * as CollapseUtils from '../composables/container-collapse-utils'

export default class GraphState {
  protected rappidContext: RappidContext

  // Artifact type containers - artifacts are rendered in HTML inside containers
  artifactTypes: Map<number, dia.Cell> = new Map()

  // Container-to-container link types
  linkTypes: Map<number, joint.dia.Link> = new Map()

  public static readonly PROP_LINK = 'link'
  public static readonly PROP_LINK_TYPE = 'linkType'
  public static readonly PROP_ARTIFACT = 'artifact'
  public static readonly PROP_ARTIFACT_TYPE = 'artifactType'

  constructor(
    private graph: dia.Graph,
    private layouter: GraphLayout,
    ctx: RappidContext,
  ) {
    this.rappidContext = ctx
    graph.on('change:hidden', (cell) => {
      cell.attr('./opacity', cell.get('hidden') ? 0 : 1)
      cell.attr('./pointer-events', cell.get('hidden') ? 'none' : 1)
    })
    graph.on('change:collapsed', (cell: dia.Element) => {
      // Only adjust containers in the same column, not the full layout
      this.layouter.layoutColumn(cell)
    })
  }

  clear(): this {
    if (this.artifactTypes.size > 0) this.artifactTypes.clear()
    if (this.linkTypes.size > 0) this.linkTypes.clear()

    if (this.graph.getCells().length > 0) this.graph.clear()

    return this
  }

  clearArtifactsAndLinks(): void {
    this.collapseAllArtifactTypes()
  }

  collapseAllArtifactTypes(): void {
    this.artifactTypes.forEach((at) => {
      CollapseUtils.collapseContainer(at, this.rappidContext)
    })
    const artifactCacheStore = useArtifactCacheStore()
    artifactCacheStore.artifactTypes.forEach((at) => {
      at.collapsed = true
      at.artifactCount = 0
      at.loadedPages = 1
    })
  }

  removeLinkTypesFromModel(linkTypes: LinkType[]): this {
    for (const linkType of linkTypes) {
      const link = this.linkTypes.get(linkType.id)
      if (link !== undefined) {
        link.remove()
      }

      this.linkTypes.delete(linkType.id)
    }

    return this
  }

  initializeOverview(artifactTypes: ArtifactType[], linkTypes: LinkType[]): this {
    let cells: dia.Cell[] = []

    artifactTypes
      // Only consider artifact types for which no JointJS element has been created yet
      .filter((at) => this.artifactTypes.get(at.id) === undefined)
      .forEach((at: ArtifactType) => {
        at.loadedPages = 0

        const typeContainer = makeTypeContainer(at)

        typeContainer.set('collapsed', true)
        typeContainer.attr('search/visibility', 'hidden')

        this.artifactTypes.set(at.id, typeContainer)

        cells.push(typeContainer)
      })

    cells = cells.concat(this.addLinkTypesToModel(linkTypes))

    this.graph.addCells(cells)

    return this
  }

  addToModel({
    artifactTypes,
    linkTypes,
  }: {
    artifacts?: Artifact[]
    artifactTypes: ArtifactType[]
    linkTypes?: LinkType[]
  }): this {
    // Sync container states based on artifact types
    // Artifacts are rendered in HTML via the ArtifactListScroller custom element
    this.syncContainerStates(artifactTypes)

    // Add link types (container-to-container links only)
    this.graph.addCells(this.addLinkTypesToModel(linkTypes ?? []))

    return this
  }

  /**
   * Sync container collapsed/expanded states based on artifact types.
   * Artifacts are now rendered in HTML inside containers, not as JointJS cells.
   */
  protected syncContainerStates(artifactTypes: ArtifactType[]): void {
    for (const artifactType of artifactTypes) {
      const typeContainer = this.artifactTypes.get(artifactType.id)
      if (!typeContainer) continue

      // Sync container state with artifact type
      if (!artifactType.collapsed) {
        typeContainer.set('collapsed', false)
        typeContainer.attr('search/visibility', 'visible')
        typeContainer.attr('artifactList/visibility', 'visible')
      }

      // Special case for no search results
      if (artifactType.containerSearchArtifactCount === 0) {
        ;(typeContainer as any).resize(typeContainer.attributes.original_width, 150)
      }
    }
  }

  protected addLinkTypesToModel(linkTypes: LinkType[]): dia.Cell[] {
    return (
      linkTypes
        // Only consider link types for which no JointJS element has been created yet
        .filter((lt) => this.linkTypes.get(lt.id) === undefined)
        // Only consider link types for which both source and target JointJS element are present
        .filter(
          (lt) =>
            this.artifactTypes.has(lt.participantA?.id as number) &&
            this.artifactTypes.has(lt.participantB?.id as number),
        )
        .map((lt) => {
          const linkSource = this.artifactTypes.get(lt.participantA?.id as number) as joint.dia.Cell
          const linkTarget = this.artifactTypes.get(lt.participantB?.id as number) as joint.dia.Cell

          const link = makeLink(lt, linkSource, linkTarget)

          this.linkTypes.set(lt.id, link)

          return link
        })
    )
  }

  filterModel({
    artifactTypes,
    linkTypes,
  }: {
    artifacts?: Artifact[]
    artifactTypes: ArtifactType[]
    linkTypes?: LinkType[]
  }): this {
    this.clearArtifactsAndLinks()

    // Sync container states and expand them for search mode
    for (const artifactType of artifactTypes) {
      const typeContainer = this.artifactTypes.get(artifactType.id)
      if (typeContainer) {
        typeContainer.set('collapsed', false)
        CollapseUtils.expandContainer(typeContainer, this.rappidContext)
        artifactType.collapsed = false
      }
    }

    // Add link types (container-to-container links only)
    this.graph.addCells(this.addLinkTypesToModel(linkTypes ?? []))

    return this
  }

  layout(): void {
    this.layouter.layout()
  }

  /**
   * Layout only containers in the same column as the given element.
   * Use this after expand/collapse to avoid moving other columns.
   */
  layoutColumn(element: dia.Element): void {
    this.layouter.layoutColumn(element)
  }
}
